import { IApiGroupCaseConfig } from "../../../interfaces/api/group/case/config/IApiGroupCaseConfig";
import { IApiGroupCase } from "../../../interfaces/api/group/case/IApiGroupCase";
import { IClient } from "../../../../common/interfaces/IClient";
import { IMapper } from "../../../interfaces/IMapper";
import { IQuoteSettingsContainerBase } from "../../../../common/interfaces/settings/IQuoteSettingsContainerBase";
import { QuoteSettingContainerType } from "../../../../common/interfaces/settings/QuoteSettingContainerType";
import { Unarray } from "../../../types/UtilityTypes";
import EnumMapper from "../EnumMapper";
import { getAllSelectedClientsIds } from "./BaseClientMapper";
import AddOnMapper from "./ClassMappers/AddOnMapper";

export function assembleMapper<T extends QuoteSettingContainerType>(
    remoteToLocal: IMapper<T>["remoteToLocal"],
    localToRemote: IMapper<T>["localToRemote"]
): IMapper<T> {
    return { remoteToLocal, localToRemote };
}

// -------------------------------------
// Remote To Local Mapping
// -------------------------------------

function baseRemoteSettingToLocal(setting: IApiGroupCase): IQuoteSettingsContainerBase {
    return {
        addons: AddOnMapper.remoteToLocal(setting.providerAddOns),
        allowChildren: false,
        caseId: setting.caseId,
        clients: [],
        isLatestVersion: setting.isLatestVersion,
        paymentFrequency: EnumMapper.PaymentFrequency.remoteToLocal(setting.frequency),
        productType: EnumMapper.Product.remoteToLocal(setting.productType),
        sortBy: EnumMapper.SortBy.remoteToLocal(setting.sortBy),
        versionId: setting.versionId
    };
}

// Typing : <Target, Sounce>

// remoteToLocal
export function remoteToLocalMapper<
    T extends IQuoteSettingsContainerBase,
    S extends IApiGroupCase
>(
    setting: S,
    settingExtensionsMapper: (
        baseSettings: IQuoteSettingsContainerBase,
        extension: S
    ) => T
): T {
    const baseSettings = baseRemoteSettingToLocal(setting);

    return settingExtensionsMapper(baseSettings, setting);
}

// -------------------------------------
// Local To Remote Mapping
// -------------------------------------

function baseLocalSettingToRemote(
    setting: QuoteSettingContainerType
): IApiGroupCaseConfig {
    return {
        clients: [],
        paymentFrequency: EnumMapper.PaymentFrequency.localToRemote(
            setting.paymentFrequency
        ),
        providerAddons: AddOnMapper.localToRemote(setting.addons),
        sortBy: setting.sortBy
    };
}

export function localToRemoteMapper<
    T extends IApiGroupCaseConfig,
    S extends QuoteSettingContainerType
>(
    clients: IClient[],
    setting: S,
    settingExtensionsMapper: (
        baseSetting: IApiGroupCaseConfig,
        extension: S,
        clients: Unarray<T["clients"]>[]
    ) => T,
    clientMapper: (client: IClient, setting: S) => Unarray<T["clients"]>
): T {
    const baseSetting = baseLocalSettingToRemote(setting);

    const selectedClientIds = getAllSelectedClientsIds(setting);

    const selectedClients = clients.filter((client) =>
        selectedClientIds.includes(client.personId)
    );

    const clientList = selectedClients.map((client) => {
        return clientMapper(client, setting);
    });

    clientList;

    return settingExtensionsMapper(baseSetting, setting, clientList);
}
